import { useState, useEffect } from "react";
import ImpactReport from "./ImpactReport";
import "./ReceiptPrinter.scss";

function ReceiptPrinter({
  modelUsed,
  numberOfCranks,
  crankingTime,
  energyGenerated,
  question,
  responseText,
  showReceipt = false,
  visibleLines = 0,
}) {
  const today = new Date();
  const formattedDate = `${today.getFullYear()}/${String(
    today.getMonth() + 1
  ).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}`;
  const formattedTime = `${String(today.getHours()).padStart(2, "0")}:${String(
    today.getMinutes()
  ).padStart(2, "0")}:${String(today.getSeconds()).padStart(2, "0")}`;

  const defaultQuestion = "How to make spaghetti?";

  const defaultResponseText = `Start with one of the simplest and most "actually Italian" things: spaghetti al pomodoro made with care. It's basic, but if you nail this, you understand authentic pasta.

I'll walk you through:
a proper sauce
how to cook pasta correctly
how to marry them (the part most people skip)

1. Ingredients (2 people)

Pasta:
180–200 g good-quality dry spaghetti

Sauce:
2–3 tbsp extra virgin olive oil
1 clove garlic, lightly crushed
1 can (400 g) whole peeled tomatoes or 3–4 ripe plum tomatoes, peeled & chopped
A small pinch of red pepper flakes (optional)
Salt
3–4 fresh basil leaves

2. Make the Sauce

Heat olive oil in a wide pan over medium-low heat. Add the garlic clove and let it gently sizzle for about 1–2 minutes. Don't let it brown — just infuse the oil. Remove the garlic (or leave it if you like stronger flavor).

Add the tomatoes. If using whole canned, crush them by hand into the pan. Add a pinch of salt and the red pepper flakes.

Let it simmer on low for 15–20 minutes, stirring occasionally. You want it to reduce slightly and become a rich, thick sauce. Tear in the basil leaves in the last few minutes.

3. Cook the Pasta

Bring a large pot of water to a rolling boil. Add salt generously — it should taste like the sea.

Cook the spaghetti for 1–2 minutes LESS than the package says (al dente). Before draining, save a cup of the starchy pasta water.

4. Marry Pasta and Sauce

This is the crucial step most people skip. Add the drained pasta directly into the sauce pan. Toss over medium heat for 1–2 minutes, adding splashes of pasta water as needed.

The starch in the water emulsifies with the oil and sauce, creating a silky coating on every strand. Finish with a drizzle of fresh olive oil.

Serve immediately. No cheese needed — this is pure pomodoro. But if you must, a little Parmigiano is ok.

Buon appetito!`;

  const textToShow = responseText || defaultResponseText;
  const totalLines = textToShow.split("\n").length;
  const isComplete = visibleLines >= totalLines;

  const [summaryVisibleItems, setSummaryVisibleItems] = useState(0);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  useEffect(() => {
    if (!isComplete) {
      setSummaryVisibleItems(0);
      setNudgeOpen(false);
      return;
    }
    setSummaryVisibleItems(0);
    let count = 0;
    const timer = setInterval(() => {
      count++;
      setSummaryVisibleItems(count);
      if (count >= 6) clearInterval(timer);
    }, 800);
    return () => clearInterval(timer);
  }, [isComplete]);

  useEffect(() => {
    if (summaryVisibleItems >= 1) setNudgeOpen(true);
  }, [summaryVisibleItems]);

  const energyValue = parseFloat(energyGenerated) || 0;

  return (
    <>
      <div className="receipt-printer">
        <div className="printer-body">
          {/* Left column: receipt paper + refresh + paper slot */}
          <div className="printer-left-col">
            {showReceipt && (
              <div className="receipt-paper">
                <div className="receipt-title-section">
                  <div className="receipt-header">
                    <div className="receipt-title">Embodied Intelligence</div>
                    <div className="receipt-date">
                      {formattedDate} {formattedTime}
                    </div>
                  </div>
                  <div className="receipt-question">
                    <span className="receipt-label">Question:</span>
                    <span className="receipt-text">
                      {question || defaultQuestion}
                    </span>
                  </div>
                  <div className="receipt-answer-label">
                    <span className="receipt-label">Answer:</span>
                  </div>
                </div>

                {visibleLines > 0 && (
                  <div className="receipt-answer">
                    <div className="receipt-content">
                      {textToShow.split("\n").slice(0, visibleLines).join("\n")}
                    </div>
                  </div>
                )}

                {isComplete && (
                  <div className="receipt-complete">
                    {summaryVisibleItems >= 1 && (
                      <div className="complete-title">Generation Complete!</div>
                    )}
                    {summaryVisibleItems >= 2 && (
                      <div className="complete-message">
                        Thank you for powering this response with your energy.
                      </div>
                    )}
                    <div className="complete-stats">
                      {summaryVisibleItems >= 3 && (
                        <div className="stat-row">
                          <span className="stat-label">&gt; model used</span>
                          <span className="stat-value">{modelUsed || "ChatGPT-5.1"}</span>
                        </div>
                      )}
                      {summaryVisibleItems >= 4 && (
                        <div className="stat-row">
                          <span className="stat-label">&gt; number of cranks</span>
                          <span className="stat-value">{numberOfCranks ?? 0}</span>
                        </div>
                      )}
                      {summaryVisibleItems >= 5 && (
                        <div className="stat-row">
                          <span className="stat-label">&gt; cranking time</span>
                          <span className="stat-value">{crankingTime || "00:00:00"}</span>
                        </div>
                      )}
                      {summaryVisibleItems >= 6 && (
                        <div className="stat-row">
                          <span className="stat-label">&gt; energy generated</span>
                          <span className="stat-value">{energyGenerated || "0 J"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button className="refresh-button">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
                  fill="white"
                />
              </svg>
              <span>Refresh</span>
            </button>

            <div className="paper-slot">
              <div className="slot-inner"></div>
            </div>
          </div>

          {/* Right column: info screen */}
          <div className="info-screen">
            {isComplete ? (
              <div className="info-complete">
                <div className="complete-ready-message">
                  Your AI Impact Summary is ready
                </div>
                <button
                  className={`open-summary-button${nudgeOpen ? " nudge" : ""}`}
                  onClick={() => setIsReportOpen(true)}
                >
                  Open
                </button>
              </div>
            ) : (
              <div className="info-content">
                <div className="info-row">
                  <span className="info-label">&gt; model using</span>
                  <span className="info-value">{modelUsed || "ChatGPT-5.1"}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">&gt; number of cranks</span>
                  <span className="info-value">{numberOfCranks ?? 0}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">&gt; cranking time</span>
                  <span className="info-value">{crankingTime || "00:00:00"}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">&gt; energy generated</span>
                  <span className="info-value">{energyGenerated || "0 J"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ImpactReport
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        energyJ={energyValue}
        question={question}
        modelUsed={modelUsed}
      />
    </>
  );
}

export default ReceiptPrinter;